class PredictiveSearch extends SearchForm {
  static cache = new Map();
  static nextId = 0;

  constructor() {
    super();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.initialTemplate = this.querySelector('template[data-search-start]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.instanceId = `search-suggestions-${++PredictiveSearch.nextId}`;
    this.searchTerm = '';
    this.requestId = 0;
    this.isOpen = false;
    this.input.setAttribute('aria-controls', this.instanceId);
    this.input.setAttribute('aria-owns', this.instanceId);
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    // Invalidate immediately, before the inherited debounce can start a new request.
    this.input.addEventListener('input', () => {
      this.cancelRequest();
      this.removeAttribute('results');
      this.close();
    });
    this.addEventListener('focusout', () => setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.cancelRequest();
        this.close();
      }
    }));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  disconnectedCallback() {
    this.cancelRequest();
  }

  cancelRequest() {
    this.requestId++;
    this.abortController?.abort();
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();
    if (!this.contains(document.activeElement)) return;
    if (!this.searchTerm) return this.showStart();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery()) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    this.cancelRequest();
    this.searchTerm = '';
    this.removeAttribute('results');
    this.showStart();
  }

  onFocus() {
    this.searchTerm = this.getQuery();
    if (!this.searchTerm) return this.showStart();
    this.getSearchResults(this.searchTerm);
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      // Let a second Escape close the containing modal.
      if (this.isOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.cancelRequest();
        this.close();
      }
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) this.onFocus();
      this.switchOption(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter' && this.isOpen && event.target === this.input) {
      const option = this.querySelector('[role="option"][aria-selected="true"]');
      if (option) {
        event.preventDefault();
        (option.matches('a, button') ? option : option.querySelector('a, button'))?.click();
      }
    }
  }

  switchOption(step) {
    const options = [...this.predictiveSearchResults.querySelectorAll('[role="option"]')]
      .filter(option => option.offsetParent !== null);
    if (!options.length) return;
    const selected = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
    const index = selected < 0 ? (step > 0 ? 0 : options.length - 1) : (selected + step + options.length) % options.length;
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.input.setAttribute('aria-activedescendant', options[index].id);
    options[index].scrollIntoView({ block: 'nearest' });
  }

  showStart() {
    this.cancelRequest();
    if (!this.initialTemplate) return this.close();
    const content = document.createElement('div');
    content.innerHTML = this.initialTemplate.innerHTML;
    const list = content.querySelector('ul');
    if (list) {
      list.id = 'predictive-search-results';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', this.input.labels?.[0]?.textContent || 'Search');
      list.querySelectorAll('li').forEach((item, index) => {
        item.id = `start-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
      });
    }
    this.renderSearchResults(content.innerHTML, false);
  }

  async getSearchResults(searchTerm) {
    this.cancelRequest();
    const requestId = this.requestId;
    const queryKey = searchTerm; // Keep spaces, case and punctuation distinct.
    if (PredictiveSearch.cache.has(queryKey)) {
      this.renderSearchResults(PredictiveSearch.cache.get(queryKey));
      return;
    }
    this.abortController = new AbortController();
    this.predictiveSearchResults.replaceChildren();
    const loading = document.createElement('p');
    loading.className = 'search-discovery-message';
    loading.textContent = this.dataset.loadingText;
    this.predictiveSearchResults.append(loading);
    this.setAttribute('loading', 'true');
    this.setLiveRegionText(this.dataset.loadingText);
    this.open();
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,query',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    });
    try {
      const response = await fetch(`${routes.predictive_search_url}?${params}`, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Search ${response.status}`);
      const html = await response.text();
      if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Missing search section');
      PredictiveSearch.cache.set(queryKey, section.innerHTML);
      if (PredictiveSearch.cache.size > 30) PredictiveSearch.cache.delete(PredictiveSearch.cache.keys().next().value);
      this.renderSearchResults(section.innerHTML);
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.requestId) return;
      this.predictiveSearchResults.replaceChildren();
      const message = document.createElement('p');
      message.className = 'search-discovery-message';
      message.textContent = this.dataset.errorText;
      const button = document.createElement('button');
      button.type = 'submit';
      button.className = 'predictive-search__item search-discovery-submit';
      button.textContent = this.dataset.allText;
      this.predictiveSearchResults.append(message, button);
      this.removeAttribute('loading');
      this.setLiveRegionText(message.textContent);
      this.open();
    }
  }

  renderSearchResults(markup, hasResults = true) {
    this.predictiveSearchResults.innerHTML = markup;
    // Cached server markup is shared; IDs and references belong to one input only.
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => {
      const old = element.id;
      element.id = old === 'predictive-search-results' ? this.instanceId : `${this.instanceId}-${old}`;
      ids.set(old, element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(' ').map(id => ids.get(id) || id).join(' '));
    });
    this.input.removeAttribute('aria-activedescendant');
    this.toggleAttribute('results', hasResults);
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
    this.open();
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
  }

  open() {
    const viewportBottom = window.visualViewport ? window.visualViewport.height + window.visualViewport.offsetTop : window.innerHeight;
    const top = this.input.getBoundingClientRect().bottom;
    this.predictiveSearchResults.style.maxHeight = `${Math.max(100, viewportBottom - top - 16)}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.cancelRequest();
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.querySelectorAll('[aria-selected="true"]').forEach(option => option.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) { this.close(clearSearchTerm); }
}
customElements.define('predictive-search', PredictiveSearch);
